/* ==========================================================================
   LaunchFlow: EcoSmart Fire product pages (v17)

   Four concerns, one file:
     1. progressive-enhancement flag
     2. variant switching (price / SKU / availability / gallery / sticky bar)
     3. add-on disclosures
     4. one-press multi-item add to cart, with a partial-failure fallback
     5. the sticky add-to-cart bar

   No localStorage / sessionStorage anywhere — theme constraint #10.

   Money is never formatted here. Every price the page can show is rendered
   server-side through the `money` filter into the variant JSON, so the shop's
   own currency format is respected and there is no second implementation of
   it to drift.
   ========================================================================== */

(function () {
  'use strict';

  /* --- 1. Progressive-enhancement flag ---------------------------------- */

  /* The theme hardcodes <html class="js">, so there is no no-js hook to hang
     this on. `.lf-fp-js` is added here instead: it is what reveals the add-on
     block. Without JS the block stays hidden and a <noscript>-style note
     explains why, rather than the page offering add-ons that a native form
     post would silently drop. */
  function flag(root) {
    root.classList.add('lf-fp-js');
  }

  /* --- helpers ---------------------------------------------------------- */

  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[lf-fp] bad JSON payload', e);
      return null;
    }
  }

  function cartTarget() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function sectionsToRender() {
    var cart = cartTarget();
    if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
    return cart.getSectionsToRender().map(function (s) {
      return s.id;
    });
  }

  /* ======================================================================
     2. Variant switching
     ====================================================================== */

  function Variants(root) {
    this.root = root;
    this.data = parseJSON(root.querySelector('[data-lf-fp-variants]')) || [];
    this.form = root.querySelector('[data-lf-fp-form]');
    this.idInput = root.querySelector('[data-lf-fp-id]');
    this.current = null;

    this.optionInputs = Array.prototype.slice.call(
      root.querySelectorAll('[data-lf-fp-option]')
    );

    /* v25 r6 — which option slot holds Fuel Type, published by
       sections/lf-fp-main.liquid as a data attribute rather than sniffed from
       label text (see the note there). 0-based to match variant.options;
       -1 means this product has no Fuel Type option, which is every product
       that hasn't been consolidated and every product that never will be
       (Stix, and all 24 accessories). Every fuel-aware branch below is
       written to be a no-op at -1. */
    this.fuelIndex = parseInt(root.dataset.lfFpFuelPos, 10) - 1;
    if (isNaN(this.fuelIndex)) this.fuelIndex = -1;
    this.gasValue = root.dataset.lfFpGasValue || 'Gas - LP/NG';

    /* v25 r11 — which option slot holds Colour, and which media id each
       colour VALUE should put on the stage. Both are published by
       sections/lf-fp-main.liquid; see the long note there for how the map is
       resolved. -1 means the product has no Colour option at all, and every
       branch below is written to be a no-op at -1 so the accessories and any
       other template sharing this script keep their original behaviour. */
    this.colorIndex = parseInt(root.dataset.lfFpColorPos, 10) - 1;
    if (isNaN(this.colorIndex)) this.colorIndex = -1;
    this.colorMedia = parseJSON(root.querySelector('[data-lf-fp-color-media]')) || {};
    this.lastColor = null;

    this.bind();
    this.sync(true);
  }

  /* The fuel a given variant is, or null on a product that doesn't sell two.
     Null and "Ethanol" are deliberately different answers: null means the
     question does not apply, and callers use it to leave fuel-dependent UI
     exactly as the page shipped it. */
  Variants.prototype.fuelOf = function (variant) {
    if (this.fuelIndex < 0 || !variant || !variant.options) return null;
    return variant.options[this.fuelIndex] || null;
  };

  Variants.prototype.bind = function () {
    var self = this;
    this.optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        self.sync(false);
      });
    });
  };

  /* Selected values, indexed by option position (1-based in Liquid, 0-based
     here). Option order is whatever the product defines — Color is in slot 1
     so it can bind to shopify.color-pattern — and is deliberately independent
     of the order the pickers are PAINTED in, which puts Application first. */
  Variants.prototype.selected = function () {
    var vals = [];
    this.optionInputs.forEach(function (input) {
      if (!input.checked) return;
      vals[parseInt(input.dataset.lfFpOption, 10) - 1] = input.value;
    });
    return vals;
  };

  Variants.prototype.find = function (vals) {
    for (var i = 0; i < this.data.length; i++) {
      var v = this.data[i];
      var ok = true;
      for (var j = 0; j < vals.length; j++) {
        if (vals[j] === undefined) continue;
        if (v.options[j] !== vals[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return v;
    }
    return null;
  };

  /* Grey out option values that cannot combine with the rest of the current
     selection.
     -------------------------------------------------------------------
     v25 r6 splits what used to be one verdict into two, because the two
     reasons a combination can't be bought are not the same kind of fact:

     OUT OF STOCK is temporary and inventory-driven. It stays exactly as it
     was — aria-disabled, still clickable — so a customer is never trapped in
     a corner of the option matrix they cannot click out of, and so they can
     still see the price and the sold-out message for the thing they wanted.

     IMPOSSIBLE means the variant does not exist and never will: Indoor +
     Gas - LP/NG, because a gas burner cannot be certified for indoor use.
     Offering it as a clickable square that resolves to "Unavailable" invites
     the customer to try, fail, and conclude the site is broken. So it is
     genuinely disabled — out of the tab order, unclickable by mouse, keyboard
     and screen reader alike — and painted grey rather than merely dimmed.
     This is what mad-usa's own configurator does with the same pair.

     Nothing traps anyone: the exclusion is symmetric and only ever disables
     the OTHER axis, so Outdoor is always one click away from re-enabling Gas
     and Ethanol is always one click away from re-enabling Indoor. And the
     currently-checked value is never disabled — probing it reproduces the
     current selection, which by definition exists.

     The same verdict is mirrored onto the sticky bar's <select>s. Without
     that, the bar's change handler would happily script `checked = true` onto
     a disabled radio (the platform permits it) and walk straight back into
     the combination this function just refused. */
  Variants.prototype.markUnavailable = function (vals) {
    var self = this;
    var impossible = {};

    this.optionInputs.forEach(function (input) {
      var pos = parseInt(input.dataset.lfFpOption, 10) - 1;
      var probe = vals.slice();
      probe[pos] = input.value;
      var match = self.find(probe);
      var exists = !!match;
      var usable = !!(match && match.available);

      input.setAttribute('aria-disabled', usable ? 'false' : 'true');
      input.disabled = !exists;
      if (!exists) impossible[pos + ' ' + input.value] = true;

      var label = input.nextElementSibling;
      if (label) {
        label.classList.toggle('lf-fp-unavailable', !usable);
        label.classList.toggle('lf-fp-impossible', !exists);
      }
    });

    this.root.querySelectorAll('[data-lf-fp-sticky-opt]').forEach(function (select) {
      var pos = parseInt(select.dataset.lfFpStickyOpt, 10) - 1;
      Array.prototype.forEach.call(select.options, function (opt) {
        opt.disabled = !!impossible[pos + ' ' + opt.value];
      });
    });
  };

  /* The grey value printed beside each option's own label — "APPLICATION
     Outdoor". It was rendered once by Liquid from the initial variant and
     then never touched again, so on every page where a picker was changed it
     went on stating the value the page had LOADED with while the squares
     underneath showed something else. Read off the radios rather than off the
     matched variant, so it stays truthful even for a combination that matches
     no variant at all. */
  Variants.prototype.setOptionLabels = function (vals) {
    this.root.querySelectorAll('[data-lf-fp-opt-value]').forEach(function (el) {
      var pos = parseInt(el.dataset.lfFpOptValue, 10) - 1;
      if (vals[pos] !== undefined) el.textContent = vals[pos];
    });
  };

  Variants.prototype.sync = function (initial) {
    var vals = this.selected();
    var variant = this.find(vals);

    this.markUnavailable(vals);
    this.setOptionLabels(vals);

    if (!variant) {
      this.setUnavailable();
      return;
    }

    this.current = variant;
    if (this.idInput) this.idInput.value = variant.id;

    this.setText('[data-lf-fp-price]', variant.price);
    this.setText('[data-lf-fp-compare]', variant.compare_at || '');
    this.toggle('[data-lf-fp-compare]', !!variant.compare_at);
    this.setText('[data-lf-fp-sku]', variant.sku || '');
    this.toggle('[data-lf-fp-sku]', !!variant.sku);

    this.setStock(variant);
    this.setButton(variant);
    this.setGallery(variant, initial);
    this.setSticky(variant);

    /* Keep the address bar honest so a shared link lands on the same variant,
       and so back/forward inside the page behaves. replaceState, not
       pushState — a colour change is not a history entry. */
    /* try/catch, added v25 r6. replaceState is rate-limited by the platform
       — Safari throws after ~100 calls in 30 seconds — and a throw here used
       to abort the rest of sync(), which now includes the 'lf-fp:variant'
       dispatch that the Fuel Type rule and the Included Accessories swap both
       hang off. A cosmetic address bar must not be able to take the page's
       state with it. Caught rather than guarded because the limit is not
       observable from script. */
    if (!initial && variant.id) {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        /* Address bar stays on the previous variant. Nothing else cares. */
      }
    }

    /* Let Dawn's own listeners (pickup availability, app blocks) react.
       `fuel` rides along so sections OUTSIDE this one — Included Accessories
       lives in its own section further down the page — can react to the
       choice without each having to re-derive which option slot holds it.
       The event already bubbles, so those listeners bind on document. */
    this.root.dispatchEvent(
      new CustomEvent('lf-fp:variant', {
        bubbles: true,
        detail: { variant: variant, fuel: this.fuelOf(variant) }
      })
    );
  };

  Variants.prototype.setText = function (sel, value) {
    this.root.querySelectorAll(sel).forEach(function (el) {
      el.textContent = value;
    });
  };

  Variants.prototype.toggle = function (sel, show) {
    this.root.querySelectorAll(sel).forEach(function (el) {
      el.hidden = !show;
    });
  };

  Variants.prototype.setStock = function (variant) {
    this.root.querySelectorAll('[data-lf-fp-stock]').forEach(function (el) {
      el.setAttribute('data-in-stock', variant.available ? 'true' : 'false');
      el.textContent = variant.available
        ? el.dataset.lfFpInStock || 'In stock'
        : el.dataset.lfFpOutStock || 'Out of stock';
    });
  };

  Variants.prototype.setButton = function (variant) {
    this.root.querySelectorAll('[data-lf-fp-submit]').forEach(function (btn) {
      var text = btn.querySelector('[data-lf-fp-submit-text]');
      btn.disabled = !variant.available;
      if (text) {
        text.textContent = variant.available
          ? btn.dataset.lfFpAdd || 'Add to Cart'
          : btn.dataset.lfFpSoldOut || 'Sold out';
      }
    });
  };

  Variants.prototype.setUnavailable = function () {
    this.current = null;
    this.root.querySelectorAll('[data-lf-fp-submit]').forEach(function (btn) {
      btn.disabled = true;
      var text = btn.querySelector('[data-lf-fp-submit-text]');
      if (text) text.textContent = btn.dataset.lfFpUnavailable || 'Unavailable';
    });
  };

  /* The stage follows COLOUR, and nothing else.

     It used to follow `variant.featured_media` on every sync, so choosing
     Indoor or switching to Gas moved the main image as a side effect of the
     variant change. Hope's instruction at r11 is that only the Colour
     buttons may move it: Outdoor/Indoor and Ethanol/Gas have to leave the
     gallery exactly where the shopper left it.

     Comparing against the previously-applied colour, rather than suppressing
     specific option slots, is what makes that true for any future option a
     product gains — a third non-colour picker would be inert here for free.

     Where a colour lands is the map from lf-fp-main.liquid: the first
     photograph of that finish in the product's own media order. It falls
     back to the variant's own media where a colour has no entry, so a
     product outside the map degrades to the old behaviour rather than to a
     stage that never moves. */
  Variants.prototype.setGallery = function (variant, initial) {
    if (this.colorIndex < 0) {
      /* No Colour option — Stix, and the accessory template that shares this
         script. Untouched: original variant-driven behaviour. */
      if (initial || !variant.media_id) return;
      this.showMedia(variant.media_id);
      return;
    }

    var color = (variant.options || [])[this.colorIndex];
    if (color === undefined) color = null;

    /* Seeded on first render, never applied on it: the page already ships
       with the right slide active, and the shopper's first real colour click
       should be the first thing that ever moves the stage. */
    if (initial) {
      this.lastColor = color;
      return;
    }

    /* An Application or Fuel Type change arrives here with the colour
       unchanged, and is deliberately a no-op. */
    if (color === this.lastColor) return;
    this.lastColor = color;

    var mediaId =
      color !== null && Object.prototype.hasOwnProperty.call(this.colorMedia, color)
        ? this.colorMedia[color]
        : variant.media_id;
    if (mediaId) this.showMedia(mediaId);
  };

  Variants.prototype.showMedia = function (mediaId) {
    var slide = this.root.querySelector(
      '[data-lf-fp-slide][data-media-id="' + mediaId + '"]'
    );
    if (slide) showSlide(this.root, slide.dataset.lfFpSlide, true);
  };

  Variants.prototype.setSticky = function (variant) {
    var bar = this.root.querySelector('[data-lf-fp-sticky]');
    if (!bar) return;
    var price = bar.querySelector('[data-lf-fp-sticky-price]');
    if (price) price.textContent = variant.price;
  };

  /* ======================================================================
     Gallery
     ====================================================================== */

  function showSlide(root, index, scrollIntoView) {
    root.querySelectorAll('[data-lf-fp-slide]').forEach(function (slide) {
      slide.dataset.active = slide.dataset.lfFpSlide === index ? 'true' : 'false';
    });
    root.querySelectorAll('[data-lf-fp-thumb]').forEach(function (thumb) {
      var on = thumb.dataset.lfFpThumb === index;
      thumb.setAttribute('aria-current', on ? 'true' : 'false');
      /* A variant change can select a thumbnail that is scrolled out of the
         strip. Without this the main image changes and the carousel appears
         not to have responded at all. */
      if (on && scrollIntoView && thumb.scrollIntoView) {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });
  }

  function initGallery(root) {
    root.querySelectorAll('[data-lf-fp-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showSlide(root, thumb.dataset.lfFpThumb, false);
      });
    });

    var strip = root.querySelector('[data-lf-fp-thumbs]');
    if (strip) {
      root.querySelectorAll('[data-lf-fp-scroll]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          /* Page by roughly one strip-width, so a click always lands on new
             thumbnails rather than nudging by a fixed pixel count that means
             something different at every viewport. */
          strip.scrollBy({
            left: parseInt(btn.dataset.lfFpScroll, 10) * Math.round(strip.clientWidth * 0.8),
            behavior: 'smooth'
          });
        });
      });

      /* The arrows only exist when there is something to scroll to. */
      var syncArrows = function () {
        var overflowing = strip.scrollWidth > strip.clientWidth + 1;
        root.querySelectorAll('[data-lf-fp-scroll]').forEach(function (btn) {
          var dir = parseInt(btn.dataset.lfFpScroll, 10);
          var atStart = strip.scrollLeft <= 1;
          var atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
          btn.hidden = !overflowing || (dir < 0 ? atStart : atEnd);
        });
      };
      strip.addEventListener('scroll', syncArrows, { passive: true });
      window.addEventListener('resize', syncArrows, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(syncArrows).observe(strip);
      syncArrows();
    }

    var first = root.querySelector('[data-lf-fp-slide]');
    if (first) showSlide(root, first.dataset.lfFpSlide, false);
  }

  /* ======================================================================
     3. Add-on disclosures
     ====================================================================== */

  function initAddons(root) {
    root.querySelectorAll('[data-lf-fp-addon]').forEach(function (details) {
      var label = details.querySelector('[data-lf-fp-addon-label]');
      var price = details.querySelector('[data-lf-fp-addon-price]');
      var sumInfo = details.querySelector('[data-lf-fp-summary-info]');

      details.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (label) label.textContent = radio.dataset.lfFpAddonLabel || '';
          /* The price is a separate element so it can be aligned and sized on
             its own. "No, Thank You" carries none, which empties it. */
          if (price) price.textContent = radio.dataset.lfFpAddonPrice || '';

          /* Carry the chosen accessory's modal onto the summary's own info
             button, so the question is still askable once the panel has
             collapsed into its filled state. "No, Thank You" has no modal,
             which is what hides the button again. */
          if (sumInfo) {
            var target = radio.dataset.lfFpAddonModal || '';
            sumInfo.dataset.lfFpInfo = target;
            sumInfo.hidden = !target;
            if (target) {
              sumInfo.setAttribute(
                'aria-label',
                (sumInfo.dataset.lfFpInfoBase || 'More information') +
                  ' — ' + (radio.dataset.lfFpAddonName || '')
              );
            }
          }

          details.open = false;
          /* The summary is what the customer just acted on, so focus returns
             there rather than being dropped to <body> when the panel closes. */
          var summary = details.querySelector('summary');
          if (summary) summary.focus();
        });
      });

      /* The info button lives inside <summary>, where any click toggles the
         disclosure. preventDefault cancels that toggle — the default action is
         resolved at the end of propagation, so cancelling it from a descendant
         works and the event still reaches the delegated modal handler on the
         root.
       *
       * NOT stopPropagation. That was the first implementation and it killed
       * the modal outright: the delegated listener is bound on the section
       * root, so halting the event at the button meant the trigger was never
       * seen and clicking ? did nothing at all. Measured as
       * `dialog open: False` with no error anywhere. */
      if (sumInfo) {
        sumInfo.dataset.lfFpInfoBase = sumInfo.getAttribute('aria-label') || 'More information';
        sumInfo.addEventListener('click', function (e) {
          e.preventDefault();
        });
      }
    });
  }

  /* ======================================================================
     3b. Accessory information modal
     ====================================================================== */

  /* Native <dialog>, for four things that are free and correct here and
     tedious and subtly wrong when hand-rolled: the top layer (no ancestor's
     overflow, stacking context or sticky positioning can clip it), inertness
     of the rest of the document (a real focus trap, not a keydown loop),
     Escape-to-close, and a ::backdrop.
   *
   * Focus RETURN is the one part not left to the browser. The UA restores
   * focus to whatever was focused when showModal() ran — and a mouse user's
   * trigger button was never focused, so there would be nothing to restore
   * to and focus would land on <body>. The trigger is captured explicitly. */
  function dialogsSupported() {
    return typeof window.HTMLDialogElement === 'function' &&
      typeof window.HTMLDialogElement.prototype.showModal === 'function';
  }

  function initModals(root) {
    /* No dialog support means no modal. Rather than leave buttons that do
       nothing, they are removed from the page and from the tab order — the
       same treatment the whole add-on block gets with JS disabled. */
    if (!dialogsSupported()) {
      root.querySelectorAll('[data-lf-fp-info]').forEach(function (btn) {
        btn.hidden = true;
      });
      return;
    }

    var opener = null;

    function close(dialog) {
      if (dialog.open) dialog.close();
    }

    root.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-lf-fp-info]') : null;
      if (!trigger) return;
      var id = trigger.dataset.lfFpInfo;
      if (!id) return;
      var dialog = root.querySelector('#' + CSS.escape(id));
      if (!dialog || dialog.open) return;

      e.preventDefault();
      opener = trigger;
      dialog.showModal();
    });

    root.querySelectorAll('dialog.lf-fp-modal').forEach(function (dialog) {
      dialog.querySelectorAll('[data-lf-fp-modal-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          close(dialog);
        });
      });

      /* Backdrop click. A click on ::backdrop reports the dialog itself as the
         target; a click anywhere in the content reports a descendant. The
         dialog carries no padding of its own, so there is no dead ring around
         the box that would read as content and behave as backdrop. */
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) close(dialog);
      });

      dialog.addEventListener('close', function () {
        if (opener && document.contains(opener)) {
          /* preventScroll: the trigger is inside a panel that may have closed
             underneath the modal, and yanking the page to it is worse than
             leaving the scroll position alone. */
          try {
            opener.focus({ preventScroll: true });
          } catch (err) {
            opener.focus();
          }
        }
        opener = null;
      });
    });
  }

  /* Every currently selected add-on, as {id, quantity, label}. */
  function selectedAddons(root) {
    var out = [];
    root.querySelectorAll('[data-lf-fp-addon]').forEach(function (details) {
      var checked = details.querySelector('input[type="radio"]:checked');
      if (!checked || !checked.value) return; /* "No, Thank You" carries "" */
      out.push({
        id: parseInt(checked.value, 10),
        quantity: 1,
        label: checked.dataset.lfFpAddonName || checked.dataset.lfFpAddonLabel || 'add-on'
      });
    });
    return out;
  }

  /* ======================================================================
     4. One-press multi-item add to cart
     ====================================================================== */

  function Cart(root, variants) {
    this.root = root;
    this.variants = variants;
    this.note = root.querySelector('[data-lf-fp-note]');
    this.busy = false;

    var self = this;
    root.querySelectorAll('[data-lf-fp-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submit(form);
      });
    });
  }

  Cart.prototype.say = function (message, kind) {
    if (!this.note) return;
    this.note.hidden = !message;
    this.note.textContent = message || '';
    this.note.className = 'lf-fp-note' + (kind ? ' lf-fp-note--' + kind : '');
  };

  Cart.prototype.loading = function (on) {
    this.busy = on;
    this.root.querySelectorAll('[data-lf-fp-submit]').forEach(function (btn) {
      btn.setAttribute('aria-disabled', on ? 'true' : 'false');
      var spin = btn.querySelector('[data-lf-fp-spinner]');
      if (spin) spin.hidden = !on;
    });
  };

  /* POST one items array. Resolves with the parsed body either way; `ok`
     distinguishes a 200 from Shopify's 422. */
  Cart.prototype.post = function (items, withSections) {
    var body = { items: items };
    if (withSections) {
      var ids = sectionsToRender();
      if (ids.length) {
        body.sections = ids.join(',');
        body.sections_url = window.location.pathname;
      }
    }
    return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    });
  };

  Cart.prototype.submit = function (form) {
    if (this.busy) return;

    var variant = this.variants.current;
    if (!variant || !variant.available) {
      this.say(
        form.dataset.lfFpMsgUnavailable || 'That combination is not available right now.',
        'err'
      );
      return;
    }

    var qtyInput = this.root.querySelector('[data-lf-fp-qty]');
    var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    var parent = { id: variant.id, quantity: qty };
    var addons = selectedAddons(this.root);
    var items = [parent].concat(
      addons.map(function (a) {
        return { id: a.id, quantity: a.quantity };
      })
    );

    var self = this;
    this.say('');
    this.loading(true);

    /* Fast path: one call, everything as independent line items, no grouping
       property of any kind. */
    this.post(items, true)
      .then(function (res) {
        if (res.ok) {
          self.finish(res.data);
          return;
        }
        /* Shopify rejects the WHOLE items array if any single line fails, so
           nothing was added. Recovering the parent alone is the contract; the
           add-ons are then retried one at a time so that a single out-of-stock
           screen does not also cost the customer the fuel and the cover, and
           so the message can name exactly what was dropped rather than
           guessing from a 422 description. */
        if (!addons.length) {
          self.fail(res.data);
          return;
        }
        return self.recover(parent, addons);
      })
      .catch(function (e) {
        console.error('[lf-fp] add to cart failed', e);
        self.say(
          form.dataset.lfFpMsgNetwork ||
            'We could not reach the cart. Please check your connection and try again.',
          'err'
        );
      })
      .finally(function () {
        self.loading(false);
      });
  };

  Cart.prototype.recover = function (parent, addons) {
    var self = this;
    var added = [];
    var failed = [];

    return this.post([parent], false)
      .then(function (res) {
        if (!res.ok) {
          self.fail(res.data);
          return null;
        }
        /* Sequential, not parallel: /cart/add.js serialises per session
           anyway, and a burst of concurrent writes to the same cart is how
           you get a lost update. */
        return addons.reduce(function (chain, addon) {
          return chain.then(function () {
            return self.post([{ id: addon.id, quantity: addon.quantity }], false).then(
              function (r) {
                (r.ok ? added : failed).push(addon.label);
              }
            );
          });
        }, Promise.resolve());
      })
      .then(function (aborted) {
        if (aborted === null) return;
        /* Pick up the re-rendered cart sections so the drawer opens showing
           what actually landed. This goes through the Section Rendering API
           rather than another POST — /cart/add.js with an empty items array is
           not a refresh, it is a malformed add. */
        return self.fetchSections();
      })
      .then(function (res) {
        if (!failed.length) return;
        var msg =
          'Added to your cart, but ' +
          listify(failed) +
          (failed.length === 1 ? ' is' : ' are') +
          ' currently unavailable and ' +
          (failed.length === 1 ? 'was' : 'were') +
          ' not added.';
        self.say(msg, 'warn');
        /* Deliberately NOT a page reload when the sections cannot be
           re-fetched. Reloading throws away the message that was just written
           explaining what failed — which is the one thing this path exists to
           deliver. A cart bubble that is briefly stale is a far smaller
           problem than a customer who never learns the cover was dropped. */
        if (res && res.ok) {
          self.refresh(res.data);
        } else {
          var cart = cartTarget();
          if (cart && typeof cart.open === 'function') cart.open();
        }
      });
  };

  /* GET the cart sections through the Section Rendering API. Returns the same
     {ok, data} shape as post() so callers do not have to special-case it. */
  Cart.prototype.fetchSections = function () {
    var ids = sectionsToRender();
    if (!ids.length) return Promise.resolve(null);
    return fetch('/?sections=' + ids.join(','))
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        return { ok: true, data: { sections: sections } };
      })
      .catch(function () {
        return null;
      });
  };

  Cart.prototype.fail = function (data) {
    this.say(
      (data && (data.description || data.message)) ||
        'We could not add this to your cart. Please try again.',
      'err'
    );
  };

  Cart.prototype.finish = function (data) {
    this.refresh(data);
  };

  /* Hand the response to Dawn's own drawer/notification so the cart UI stays
     one implementation. */
  Cart.prototype.refresh = function (data) {
    var cart = cartTarget();
    if (!cart) {
      window.location = (window.routes && window.routes.cart_url) || '/cart';
      return;
    }
    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    if (typeof cart.renderContents === 'function' && data && data.sections) {
      cart.renderContents(data);
    } else if (typeof cart.open === 'function') {
      cart.open();
    }
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'lf-fp',
        cartData: data
      });
    }
  };

  function listify(names) {
    if (names.length === 1) return names[0];
    if (names.length === 2) return names[0] + ' and ' + names[1];
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  /* ======================================================================
     Quantity stepper — accessory pages only. Fire features post a fixed
     hidden quantity of 1 and have no wrapper for this to bind to.
     ====================================================================== */

  function initQty(root) {
    root.querySelectorAll('[data-lf-fp-qty-wrap]').forEach(function (wrap) {
      var input = wrap.querySelector('[data-lf-fp-qty]');
      if (!input) return;
      wrap.querySelectorAll('[data-lf-fp-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var step = parseInt(btn.dataset.lfFpStep, 10);
          var min = parseInt(input.min, 10) || 1;
          input.value = Math.max(min, (parseInt(input.value, 10) || min) + step);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ======================================================================
     5. Sticky add-to-cart bar
     ====================================================================== */

  /* The site header is position:sticky top:0 z-index:9999 and its height moves
     across four breakpoints with the logo settings, so the bar's offset is
     measured rather than guessed. A ResizeObserver keeps it correct through
     logo-size changes in the theme editor and through orientation changes. */
  function trackHeader() {
    var header = document.querySelector('.lf-header');
    if (!header) return;

    var apply = function () {
      document.documentElement.style.setProperty(
        '--lf-fp-header-h',
        header.offsetHeight + 'px'
      );
    };
    apply();

    if (window.ResizeObserver) {
      new ResizeObserver(apply).observe(header);
    } else {
      window.addEventListener('resize', apply, { passive: true });
    }
  }

  function initSticky(root) {
    var bar = root.querySelector('[data-lf-fp-sticky]');
    var sentinel = root.querySelector('[data-lf-fp-sticky-after]');
    if (!bar || !sentinel) return;

    /* Geometry, not IntersectionObserver.
     *
     * IO reports CROSSINGS, not states. A jump — End, an anchor link, a
     * restored scroll position, a programmatic scrollTo — takes the sentinel
     * from below the viewport to above it without ever entering it, so the
     * callback goes false -> false, which is not a crossing and fires nothing.
     * The bar then never appears for the rest of the session. Measured: after
     * a jump from 0 to 1900 past a sentinel at 1440, the observer fired
     * exactly once (at registration) and never again.
     *
     * This is the same trap the v16 reveal hit and solved with sweepPassed().
     * Here the target is 1px tall, so the window to miss is as wide as a whole
     * scroll jump, and a state read is simply the right primitive: one
     * getBoundingClientRect per animation frame, only while scrolling, and
     * correct no matter how the viewport got where it is.
     */
    var ticking = false;

    function sync() {
      ticking = false;
      var past = sentinel.getBoundingClientRect().top < 0;
      var next = past ? 'true' : 'false';
      if (bar.dataset.shown === next) return; /* no layout write when unchanged */
      bar.dataset.shown = next;
      bar.setAttribute('aria-hidden', past ? 'false' : 'true');
    }

    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(sync);
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    /* bfcache restores land here with a scroll position already applied and no
       scroll event to follow. */
    window.addEventListener('pageshow', request);
    sync();

    /* The compact selectors mirror the real pickers in both directions. */
    bar.querySelectorAll('[data-lf-fp-sticky-opt]').forEach(function (select) {
      select.addEventListener('change', function () {
        var pos = select.dataset.lfFpStickyOpt;
        var match = root.querySelector(
          '[data-lf-fp-option="' + pos + '"][value="' + CSS.escape(select.value) + '"]'
        );
        if (match) {
          match.checked = true;
          match.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    root.addEventListener('lf-fp:variant', function (e) {
      var v = e.detail.variant;
      bar.querySelectorAll('[data-lf-fp-sticky-opt]').forEach(function (select) {
        var pos = parseInt(select.dataset.lfFpStickyOpt, 10) - 1;
        if (v.options[pos] !== undefined) select.value = v.options[pos];
      });
    });

    /* The bar's button drives the real form, so there is exactly one submit
       path and one items array. */
    bar.querySelectorAll('[data-lf-fp-sticky-submit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var form = root.querySelector('[data-lf-fp-form]');
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(
          new Event('submit', { cancelable: true, bubbles: true })
        );
      });
    });
  }

  /* ======================================================================
     3c. Teak fire-screen lock
     ====================================================================== */

  /* Some finishes need the fire screen every time — currently: Color = Teak,
     for the reason in screen_lock_note (the teak surround needs the added
     protection). This is a RULE ABOUT THE COLOR VALUE, not about any one
     product: it reads variant.options[COLOR_INDEX], never a product handle
     or title, so it survives a rename of the product, the variant, or the
     screen itself without a code change. Extending it to another teak
     product with its own fire_screen add-on group needs nothing here — this
     function already runs once per [data-lf-fp-root] section and re-derives
     everything from that section's own markup and its own variant list.
     Extending the RULE to a different trigger (a second color, a different
     option position) would need this function generalized to take a list of
     trigger values, or one call per rule; that is a real code change, not a
     data change, and is deliberately not built until there is a second case
     to generalize from.

     Only the fire_screen group is ever passed a lock_note (see
     sections/lf-fp-main.liquid) — every other add-on group renders
     lf-fp-addon with no lock_note, so this function silently no-ops on a
     product that doesn't carry that group at all (`if (!group) return;`)
     and never touches fuel, burner cover, cover plate or stools.

     Locking means two things, not one: the "No, Thank You" radio has to
     become genuinely UNSELECTABLE, not merely hidden. Elsewhere in this
     theme, unchecked radios are painted with opacity: 0 rather than
     display: none specifically so they stay keyboard-reachable — Tab still
     lands on them, Space still checks them, which is normally exactly
     right. Locked, that cuts the other way: a sighted mouse user sees a
     summary that says "required," but a keyboard user tabbing through would
     still reach "No, Thank You" and could still check it, right past
     whatever the mouse-only styling implied was disabled. `.disabled = true`
     is what actually removes it from the tab order and blocks both Space
     and click, for every input method alike, rather than trusting the two
     to agree on what "locked" looks like. */
  function initTeakFireScreenLock(root, variants) {
    var COLOR_INDEX = 0; /* Color is always option 1 (index 0) — see the note
                             on Variants.prototype.selected above. */

    var group = root.querySelector('[data-lf-fp-addon] input[name*="-fire_screen-"]');
    if (!group) return; /* No fire-screen add-on on this product — nothing to lock, ever. */

    var details = group.closest('[data-lf-fp-addon]');
    var noneRadio = details.querySelector('input[value=""]');
    var pickRadio = Array.prototype.filter.call(
      details.querySelectorAll('input[type="radio"]'),
      function (r) { return r.value !== ''; }
    )[0];
    if (!pickRadio) return; /* No real choice rendered — nothing to lock to. */

    var summaryLabel = details.querySelector('[data-lf-fp-addon-label]');
    var summaryPrice = details.querySelector('[data-lf-fp-addon-price]');
    var sumInfo = details.querySelector('[data-lf-fp-summary-info]');
    var lockNote = details.parentElement.querySelector('[data-lf-fp-addon-lock-note]');

    /* Captured before this function's own first `apply()` call can overwrite
       it, so unlocking restores the exact copy the page shipped with —
       whatever `select_text` resolved to — rather than a guessed default. */
    var placeholder = {
      label: summaryLabel ? summaryLabel.textContent : '',
      price: summaryPrice ? summaryPrice.textContent : '',
      infoTarget: sumInfo ? (sumInfo.dataset.lfFpInfo || '') : '',
      infoHidden: sumInfo ? sumInfo.hidden : true
    };

    var wasTeak = false;

    function select(radio) {
      radio.checked = true;
      /* Reuses initAddons' own change handler to repaint the summary label,
         price and modal link — one code path for "an item got chosen,"
         whether the customer clicked it or the lock did. */
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Restores true "nothing selected" — the same state the block loads in
       — rather than parking on "No, Thank You". Unchecking a radio input
       via script (as opposed to a user unchecking it, which the platform
       doesn't allow) is what native radio groups support, and it's what the
       spec for this feature calls for: nothing selected, No Thank You
       merely available again. */
    function clearSelection() {
      pickRadio.checked = false;
      if (noneRadio) noneRadio.checked = false;
      if (summaryLabel) summaryLabel.textContent = placeholder.label;
      if (summaryPrice) summaryPrice.textContent = placeholder.price;
      if (sumInfo) {
        sumInfo.dataset.lfFpInfo = placeholder.infoTarget;
        sumInfo.hidden = placeholder.infoHidden;
      }
    }

    function apply(variant) {
      var isTeak = !!(variant && variant.options && variant.options[COLOR_INDEX] === 'Teak');

      if (isTeak) {
        if (noneRadio) noneRadio.disabled = true;
        /* Known, accepted gap: if the screen itself is sold out, pickRadio
           carries `disabled` too (see lf-fp-addon.liquid) and there is then
           no valid selection to lock to. Both radios stay unselectable and
           the note stays visible — correctly refusing to fake a choice —
           but nothing here can conjure inventory. Flagged for Hope rather
           than silently worked around. */
        if (!pickRadio.disabled && !pickRadio.checked) select(pickRadio);
        if (lockNote) lockNote.hidden = false;
      } else {
        if (noneRadio) noneRadio.disabled = false;
        if (wasTeak) clearSelection();
        if (lockNote) lockNote.hidden = true;
      }

      wasTeak = isTeak;
    }

    /* Variants dispatches its own first 'lf-fp:variant' synchronously inside
       `new Variants(root)`, before this function ever runs — so that first
       event is unavoidably missed by any listener added here. `apply` is
       called once, explicitly, against variants.current to cover exactly
       that case; the listener below only has to catch every change after
       this point. */
    apply(variants.current);
    root.addEventListener('lf-fp:variant', function (e) {
      apply(e.detail.variant);
    });
  }

  /* ======================================================================
     Fuel Type rule — hide the ethanol-only add-ons on a gas selection
     ======================================================================

     A consolidated listing sells the same table with either burner, so some
     of its add-on groups stop applying halfway through the configuration.
     Left on screen they are not harmless extras — they are plausible-looking
     upsells that a gas customer can add to their cart and receive.

     Two groups move, and both for the same reason: they are parts of the
     ETHANOL burner, not of the table.

       fuel  — e-NRG bioethanol is fuel for the ethanol burner and nothing
               else. A gas build runs off an LP tank or a natural gas line.

       tray  — [v25 r8] the Indoor Safety Tray is the part the INDOOR rating
               depends on, and indoor is an ethanol-only configuration: the
               Fuel Type rule in the buy block already blocks Indoor ⊕ Gas as
               an impossible combination, and the caption under the picker
               says so in as many words ("Gas burners can ONLY be used
               outdoors"). So on a gas selection the tray is an accessory for
               a configuration the same page refuses to sell. It goes.

     Everything else deliberately STAYS on both. Burner Cover is Hope's call
     and the right one: the cover protects the burner well from weather,
     which a gas build needs as much as an ethanol one. Fire Screen, the
     All-Season Cover, Glass Cover Plate and Tank Stools were never
     fuel-dependent either — they dress or protect the table itself.

     Hiding alone is not enough. An unchecked-but-hidden radio is harmless,
     but a CHECKED one is not: the customer could pick two cases of e-NRG,
     switch to gas, and post a hidden line item they can no longer see. So a
     hide also clears the group back to the state it loaded in — nothing
     selected, placeholder restored — rather than parking it on a value. This
     mirrors clearSelection() in the Teak lock below, for the same reason.

     No-op by construction on every product without a Fuel Type option:
     fuelOf() returns null there, and null is treated as "leave it alone".
     Also a no-op for any group this product does not render — the Stix has
     no tray at all, and neither branch has to know that. */
  var GAS_HIDDEN_GROUPS = ['fuel', 'tray'];

  function initFuelAddonRule(root, variants) {
    if (variants.fuelIndex < 0) return; /* Not a consolidated listing. */

    GAS_HIDDEN_GROUPS.forEach(function (key) {
      initGasHiddenGroup(root, variants, key);
    });
  }

  function initGasHiddenGroup(root, variants, key) {
    var group = root.querySelector('[data-lf-fp-addon-group="' + key + '"]');
    if (!group) return; /* This product does not render that group. */

    var details = group.querySelector('[data-lf-fp-addon]');
    var summaryLabel = group.querySelector('[data-lf-fp-addon-label]');
    var summaryPrice = group.querySelector('[data-lf-fp-addon-price]');
    var sumInfo = group.querySelector('[data-lf-fp-summary-info]');

    /* Captured before the first apply() can overwrite it, so restoring gives
       back the copy the page shipped with rather than a guessed default. */
    var placeholder = summaryLabel ? summaryLabel.textContent : '';

    function clear() {
      group.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        radio.checked = false;
      });
      if (summaryLabel) summaryLabel.textContent = placeholder;
      if (summaryPrice) summaryPrice.textContent = '';
      if (sumInfo) {
        sumInfo.dataset.lfFpInfo = '';
        sumInfo.hidden = true;
      }
      if (details) details.open = false;
    }

    function apply(variant) {
      var fuel = variants.fuelOf(variant);
      if (fuel === null) return;
      var isGas = fuel === variants.gasValue;
      if (isGas === group.hidden) return; /* Already in the right state. */
      group.hidden = isGas;
      if (isGas) clear();
    }

    /* Variants dispatches its first 'lf-fp:variant' synchronously inside its
       own constructor, before this function exists, so the initial state is
       applied explicitly here and the listener only catches changes after. */
    apply(variants.current);
    root.addEventListener('lf-fp:variant', function (e) {
      apply(e.detail.variant);
    });
  }

  /* ======================================================================
     Accessory share button
     ====================================================================== */

  function initShare(root) {
    root.querySelectorAll('[data-lf-fp-share]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.dataset.lfFpShare || window.location.href;
        var label = btn.querySelector('span');
        var done = btn.dataset.lfFpShared || 'Link copied';
        var idle = label ? label.textContent : '';

        if (navigator.share) {
          navigator.share({ title: document.title, url: url }).catch(function () {});
          return;
        }
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            if (!label) return;
            label.textContent = done;
            setTimeout(function () {
              label.textContent = idle;
            }, 2000);
          });
        }
      });
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */

  function boot() {
    trackHeader();

    document.querySelectorAll('[data-lf-fp-root]').forEach(function (root) {
      /* Idempotence guard. boot() runs on DOMContentLoaded and again on every
         shopify:section:load, and the file would be included twice if a
         template ever carried both lf-fp-main and lf-ac-main. Double-binding
         the submit handler posts the cart twice — two of everything, silently.
         Cheap to prevent, expensive to notice. */
      if (root.dataset.lfFpBooted === 'true') return;
      root.dataset.lfFpBooted = 'true';

      flag(root);
      initGallery(root);
      initQty(root);
      initAddons(root);
      initModals(root);
      initShare(root);

      if (root.querySelector('[data-lf-fp-variants]')) {
        var variants = new Variants(root);
        new Cart(root, variants);
        initSticky(root);
        initTeakFireScreenLock(root, variants);
        initFuelAddonRule(root, variants);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme-editor support: sections are replaced wholesale on edit. */
  document.addEventListener('shopify:section:load', boot);
})();
